import {UIEvent, useCallback, useEffect, useRef, useState} from 'react';
import {useSelector} from 'react-redux';
import {useParams} from 'react-router-dom';
import {RootState} from '../../../redux/store.type';
import {getSubscriptionEvents, getSubscriptionTickets} from '../../../redux/eTicket/eTicketActions';
import isScrollEnd from '../../../functions/isScrollEnd';

const PAGE_SIZE = 10;
/* Distance from the bottom of the list (px) at which the next page starts loading */
const LOAD_MORE_OFFSET = 120;

const fetchers = {
    events: getSubscriptionEvents,
    tickets: getSubscriptionTickets
};

/* Loads a subscription list page by page and accumulates it on the ticket in the store.
   The first page is fetched when the sheet is actually opened — the sheets stay mounted,
   so fetching on mount would pull every list of every series on entering the page. */
export function useSubscriptionList<K extends keyof typeof fetchers>(kind: K, qid: number, isOpen: boolean) {
    const {token} = useParams<{token: string}>();
    const items = useSelector((state: RootState) =>
        state.eTicket.data?.tickets?.find(ticket => ticket.qid === qid)?.subscription?.[kind]);
    const [isLoading, setIsLoading] = useState(false);
    const [hasError, setHasError] = useState(false);
    const [total, setTotal] = useState<number | null>(null);
    /* State settles a render too late to guard the fetch: scroll events of one frame would all
       read isLoading === false and send the same offset again, doubling that page in the store. */
    const isFetching = useRef(false);

    const loaded = items?.length ?? 0;
    // total is still unknown ⇒ the first page has not come back yet
    const hasMore = total === null || loaded < total;

    const loadMore = useCallback(async () => {
        if(!token || isFetching.current || !hasMore) {
            return;
        }
        isFetching.current = true;
        setIsLoading(true);
        setHasError(false);
        try {
            const page = await fetchers[kind](token, qid, PAGE_SIZE, loaded);
            setTotal(page.total);
        } catch {
            // the list is not closed on a failure — the sheet says so and offers the page again
            setHasError(true);
        } finally {
            isFetching.current = false;
            setIsLoading(false);
        }
    }, [kind, token, qid, loaded, hasMore]);

    useEffect(() => {
        // items === undefined ⇒ nothing loaded yet (an empty array is a valid answer, not a retry)
        if(isOpen && !items) {
            loadMore();
        }
    }, [isOpen]);

    const onScroll = (e: UIEvent<HTMLDivElement>) => {
        if(isScrollEnd(e, LOAD_MORE_OFFSET)) {
            loadMore();
        }
    };

    // after a failure the next page is fetched by the retry button, not by scrolling into it again
    return {items, isLoading, hasError, retry: loadMore, onScroll: hasMore && !hasError ? onScroll : undefined};
}
